"use client"

import type React from "react"
import { useState, useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { FileUp, Download, Trash2, Plus, FileText, Loader2, AlertCircle, GripVertical, ChevronUp, ChevronDown } from "lucide-react"
import { toast } from "sonner"
import { Progress } from "@/components/ui/progress"
import { API_URL, API_KEY } from "@/lib/api-config"

// Maximum total size: 500MB
const MAX_TOTAL_SIZE_BYTES = 500 * 1024 * 1024
// Maximum 50 PDFs
const MAX_PDF_COUNT = 50

interface PdfFile {
  id: string
  file: File
  name: string
  size: number
  pages?: number
}

interface UploadSlot {
  index: number
  uploadUrl: string
  storageKey: string
}

type ProcessingStatus = "idle" | "creating-job" | "uploading" | "processing" | "completed" | "error"

export function PdfMerge() {
  const [pdfs, setPdfs] = useState<PdfFile[]>([])
  const [outputFileName, setOutputFileName] = useState("merged")

  const [status, setStatus] = useState<ProcessingStatus>("idle")
  const [uploadProgress, setUploadProgress] = useState(0)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [resultInfo, setResultInfo] = useState<{ fileName: string; size: number; pages: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragItem = useRef<number | null>(null)
  const dragOverItem = useRef<number | null>(null)

  const totalSize = pdfs.reduce((sum, pdf) => sum + pdf.size, 0)

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const validFiles: PdfFile[] = []
    let totalNewSize = 0

    Array.from(files).forEach((file) => {
      if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
        toast.error(`${file.name} is not a PDF file`)
        return
      }

      if (pdfs.length + validFiles.length >= MAX_PDF_COUNT) {
        toast.error(`Maximum ${MAX_PDF_COUNT} PDFs allowed`)
        return
      }

      totalNewSize += file.size
      if (totalSize + totalNewSize > MAX_TOTAL_SIZE_BYTES) {
        toast.error(`Total file size exceeds ${MAX_TOTAL_SIZE_BYTES / 1024 / 1024}MB limit`)
        return
      }

      validFiles.push({
        id: Math.random().toString(36).substring(2, 9),
        file,
        name: file.name,
        size: file.size,
      })
    })

    setPdfs((prev) => [...prev, ...validFiles])
    setDownloadUrl(null)
    setResultInfo(null)
    setError(null)

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }, [pdfs.length, totalSize])

  const removePdf = useCallback((id: string) => {
    setPdfs((prev) => prev.filter((pdf) => pdf.id !== id))
    setDownloadUrl(null)
    setResultInfo(null)
  }, [])

  const clearAll = useCallback(() => {
    setPdfs([])
    setDownloadUrl(null)
    setResultInfo(null)
    setError(null)
  }, [])

  // Drag and drop reordering
  const handleDragStart = (index: number) => {
    dragItem.current = index
  }

  const handleDragEnter = (index: number) => {
    dragOverItem.current = index
  }

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return

    const reorderedPdfs = [...pdfs]
    const [draggedItem] = reorderedPdfs.splice(dragItem.current, 1)
    reorderedPdfs.splice(dragOverItem.current, 0, draggedItem)

    dragItem.current = null
    dragOverItem.current = null
    setPdfs(reorderedPdfs)
  }

  const moveItem = useCallback((index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= pdfs.length) return

    const reorderedPdfs = [...pdfs]
    const [movedItem] = reorderedPdfs.splice(index, 1)
    reorderedPdfs.splice(newIndex, 0, movedItem)
    setPdfs(reorderedPdfs)
  }, [pdfs])

  const mergePdfs = async () => {
    if (pdfs.length < 2) {
      toast.error("Please select at least 2 PDF files to merge")
      return
    }

    setStatus("creating-job")
    setError(null)
    setUploadProgress(0)

    try {
      // Step 1: Create job and get upload URLs
      const createResponse = await fetch(`${API_URL}/api/v1/image/pdf/merge`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": API_KEY,
        },
        body: JSON.stringify({
          pdfCount: pdfs.length,
          pdfs: pdfs.map((pdf) => ({
            fileName: pdf.name,
            fileSizeBytes: pdf.size,
          })),
          outputFileName,
        }),
      })

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || "Failed to create merge job")
      }

      const { jobId, uploadSlots } = await createResponse.json() as { jobId: string; uploadSlots: UploadSlot[] }

      // Step 2: Upload PDFs to presigned URLs
      setStatus("uploading")

      for (let i = 0; i < uploadSlots.length; i++) {
        const slot = uploadSlots[i]
        const pdf = pdfs[slot.index]

        const uploadResponse = await fetch(slot.uploadUrl, {
          method: "PUT",
          body: pdf.file,
          headers: {
            "Content-Type": "application/pdf",
          },
        })

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload PDF ${i + 1}`)
        }

        setUploadProgress(((i + 1) / uploadSlots.length) * 100)
      }

      // Step 3: Process and merge PDFs
      setStatus("processing")

      const processResponse = await fetch(`${API_URL}/api/v1/image/pdf/merge/${jobId}/process`, {
        method: "POST",
        headers: {
          "X-API-Key": API_KEY,
        },
      })

      if (!processResponse.ok) {
        const errorData = await processResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || "Failed to merge PDFs")
      }

      const result = await processResponse.json() as { downloadUrl: string; fileName: string; fileSizeBytes: number; pageCount: number }

      setDownloadUrl(result.downloadUrl)
      setResultInfo({
        fileName: result.fileName,
        size: result.fileSizeBytes,
        pages: result.pageCount,
      })
      setStatus("completed")
      toast.success(`PDFs merged successfully! ${result.pageCount} total pages`)
    } catch (err) {
      setStatus("error")
      const message = err instanceof Error ? err.message : "An error occurred"
      setError(message)
      toast.error(message)
    }
  }

  const downloadPdf = () => {
    if (!downloadUrl) return
    window.open(downloadUrl, "_blank")
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const isProcessing = status === "creating-job" || status === "uploading" || status === "processing"

  return (
    <Card>
      <CardHeader>
        <CardTitle>PDF Merge</CardTitle>
        <CardDescription>
          Combine multiple PDF files into a single document. Drag to reorder pages.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* File Input and Options */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              id="pdf-upload"
              disabled={isProcessing}
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="sm:flex-1"
              disabled={isProcessing}
            >
              <FileUp className="h-4 w-4 mr-2" />
              Select PDF Files
            </Button>
            <div className="sm:flex-1">
              <div className="flex items-center gap-2">
                <Label htmlFor="output-name" className="whitespace-nowrap">Output:</Label>
                <Input
                  id="output-name"
                  value={outputFileName}
                  onChange={(e) => setOutputFileName(e.target.value)}
                  placeholder="merged"
                  disabled={isProcessing}
                />
                <span className="text-muted-foreground">.pdf</span>
              </div>
            </div>
          </div>
        </div>

        {/* PDF List */}
        <div className="border rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium">
              Selected PDFs ({pdfs.length}) - {formatFileSize(totalSize)}
            </h3>
            {pdfs.length > 0 && (
              <Button variant="outline" size="sm" onClick={clearAll} disabled={isProcessing}>
                Clear All
              </Button>
            )}
          </div>

          {pdfs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mb-2" />
              <p>No PDFs selected</p>
              <Button variant="link" onClick={() => fileInputRef.current?.click()}>
                <Plus className="h-4 w-4 mr-1" />
                Add PDF Files
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {pdfs.map((pdf, index) => (
                <div
                  key={pdf.id}
                  className="flex items-center gap-3 p-3 border rounded-lg bg-background hover:bg-muted/50 transition-colors cursor-move"
                  draggable={!isProcessing}
                  onDragStart={() => handleDragStart(index)}
                  onDragEnter={() => handleDragEnter(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <GripVertical className="h-4 w-4 hidden sm:block" />
                    <div className="flex flex-col sm:hidden">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => moveItem(index, "up")}
                        disabled={isProcessing || index === 0}
                      >
                        <ChevronUp className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => moveItem(index, "down")}
                        disabled={isProcessing || index === pdfs.length - 1}
                      >
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </div>
                    <span className="w-6 text-center font-medium">{index + 1}</span>
                  </div>
                  <FileText className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{pdf.name}</p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(pdf.size)}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 flex-shrink-0"
                    onClick={() => removePdf(pdf.id)}
                    disabled={isProcessing}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Progress */}
        {isProcessing && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">
                {status === "creating-job" && "Creating merge job..."}
                {status === "uploading" && `Uploading PDFs... ${Math.round(uploadProgress)}%`}
                {status === "processing" && "Merging PDFs..."}
              </span>
            </div>
            {status === "uploading" && <Progress value={uploadProgress} />}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Result */}
        {status === "completed" && resultInfo && (
          <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">PDFs Merged Successfully!</p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {resultInfo.fileName} - {formatFileSize(resultInfo.size)} - {resultInfo.pages} pages
                </p>
              </div>
              <Button onClick={downloadPdf}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </div>
        )}

        {/* Merge Button */}
        <Button
          onClick={mergePdfs}
          disabled={pdfs.length < 2 || isProcessing}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Merge PDFs {pdfs.length >= 2 && `(${pdfs.length} files)`}
            </>
          )}
        </Button>

        {pdfs.length === 1 && (
          <p className="text-sm text-muted-foreground text-center">
            Add at least one more PDF to merge
          </p>
        )}
      </CardContent>
    </Card>
  )
}
